import { StatusPill, type StatusTone } from '@vnetwork/ui/data';
import type {
  CandidateAssignment,
  CandidateResource,
  DiscoverCandidatesParams,
} from '@/api/projects';

/** Map a resource's domain status string to a StatusPill tone. */
export function resourceStatusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case 'active':
    case 'running':
    case 'ready':
      return 'success';
    case 'pending':
    case 'provisioning':
    case 'deploying':
      return 'warning';
    case 'error':
    case 'failed':
    case 'suspended':
      return 'danger';
    default:
      return 'muted';
  }
}

const ASSIGNMENT_LABEL: Record<CandidateAssignment, string> = {
  unassigned: 'Unassigned',
  this_project: 'In this project',
  other_project: 'In other project',
};

const ASSIGNMENT_TONE: Record<CandidateAssignment, StatusTone> = {
  unassigned: 'muted',
  this_project: 'success',
  other_project: 'warning',
};

interface AssignmentPillProps {
  assignment: CandidateAssignment;
  /** Project name to surface in the tooltip for `other_project`. */
  projectName?: string;
}

/** Renders a candidate's assignment state as a tone-coded StatusPill. */
export function AssignmentPill({ assignment, projectName }: AssignmentPillProps) {
  const label = ASSIGNMENT_LABEL[assignment];
  const tone = ASSIGNMENT_TONE[assignment];
  const tooltip =
    assignment === 'other_project' && projectName
      ? `Already assigned to "${projectName}". It can belong to multiple projects.`
      : undefined;
  return (
    <StatusPill tone={tone} tooltip={tooltip}>
      {assignment === 'other_project' && projectName ? `${label}: ${projectName}` : label}
    </StatusPill>
  );
}

// ============================================
// Shared wizard state
// ============================================

/** Step 1 form state — the user-entered conditions. */
export interface WizardConditions {
  projectId: string;
  resourceTypes: ('domain' | 'instance')[];
  status: string;
  /** Raw comma/space separated tag text; parsed on submit. */
  tagsInput: string;
  search: string;
  /** `assignment` filter — limit to unassigned only, or all. */
  unassignedOnly: boolean;
  createdFrom: string;
  createdTo: string;
}

export function emptyConditions(targetProjectId?: string): WizardConditions {
  return {
    projectId: targetProjectId ?? '',
    resourceTypes: ['domain', 'instance'],
    status: '',
    tagsInput: '',
    search: '',
    unassignedOnly: false,
    createdFrom: '',
    createdTo: '',
  };
}

/** Parse the freeform tags input into a clean, de-duplicated list. */
export function parseTags(input: string): string[] {
  const seen = new Set<string>();
  for (const raw of input.split(/[,\s]+/)) {
    const t = raw.trim();
    if (t) seen.add(t);
  }
  return [...seen];
}

/** Translate UI conditions into the candidates query params. */
export function conditionsToParams(
  c: WizardConditions,
  page: number,
  perPage: number,
): DiscoverCandidatesParams {
  const tags = parseTags(c.tagsInput);
  return {
    resource_types: c.resourceTypes.length ? c.resourceTypes : undefined,
    status: c.status || undefined,
    search: c.search.trim() || undefined,
    tags: tags.length ? tags : undefined,
    assignment: c.unassignedOnly ? 'unassigned' : 'all',
    created_from: c.createdFrom ? new Date(c.createdFrom).toISOString() : undefined,
    created_to: c.createdTo ? new Date(c.createdTo).toISOString() : undefined,
    page,
    per_page: perPage,
  };
}

/** Translate UI conditions into the auto-assign server-side filter block. */
export function conditionsToFilters(c: WizardConditions) {
  const tags = parseTags(c.tagsInput);
  return {
    resource_types: c.resourceTypes.length ? c.resourceTypes : undefined,
    status: c.status || undefined,
    search: c.search.trim() || undefined,
    tags: tags.length ? tags : undefined,
    created_from: c.createdFrom ? new Date(c.createdFrom).toISOString() : undefined,
    created_to: c.createdTo ? new Date(c.createdTo).toISOString() : undefined,
  };
}

/** Stable row id used by the preview DataTable + selection map. */
export function candidateRowId(r: CandidateResource): string {
  return `${r.resource_type}:${r.resource_id}`;
}

/**
 * Controlled selection map keyed by `candidateRowId`. Structurally identical to
 * react-table's `RowSelectionState` (which @partner cannot import directly).
 */
export type RowSelection = Record<string, boolean>;
